import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, DollarSign, MapPin, Users, Plus, Trash2, Sparkles, ArrowRight, CalendarDays, Gift, Hotel } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function ItineraryBuilderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [showNewItinerary, setShowNewItinerary] = useState(false);
  const [currentItinerary, setCurrentItinerary] = useState(null);
  const [items, setItems] = useState([]);
  const [itineraryStartDate, setItineraryStartDate] = useState(null);
  const [itineraryEndDate, setItineraryEndDate] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchItineraries();
  }, [user]);

  const fetchItineraries = async () => {
    try {
      const response = await api.getItineraries();
      setItineraries(response.data);
    } catch (error) {
      console.error('Failed to fetch itineraries:', error);
    }
  };

  const handleCreateItinerary = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      const response = await api.createItinerary({
        event_name: formData.get('event_name'),
        event_type: formData.get('event_type'),
        start_date: new Date(formData.get('start_date')).toISOString(),
        end_date: new Date(formData.get('end_date')).toISOString(),
        budget: parseFloat(formData.get('budget')),
        location: formData.get('location'),
        attendees: parseInt(formData.get('attendees')),
      });

      toast.success('Itinerary created successfully!');
      setCurrentItinerary(response.data);
      setShowNewItinerary(false);
      fetchItineraries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create itinerary');
    }
  };

  const addItem = () => {
    setItems([...items, { day: 1, time: '', activity: '', cost: 0, notes: '' }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const saveItinerary = async () => {
    if (!currentItinerary) return;

    try {
      await api.updateItinerary(currentItinerary.id, items);
      toast.success('Itinerary saved successfully!');
      fetchItineraries();
    } catch (error) {
      toast.error('Failed to save itinerary');
    }
  };

  const totalCost = items.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);

  return (
    <div className="min-h-screen pt-24 pb-20 px-6 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="luxury-text text-5xl font-medium tracking-tight text-midnight-navy mb-4" data-testid="itinerary-page-title">
              AI-Powered Itinerary Builder
            </h1>
            <p className="text-lg text-slate-800">
              Plan your perfect event timeline with smart suggestions
            </p>
          </div>
          <Button
            onClick={() => setShowNewItinerary(true)}
            className="rounded-sm bg-midnight-navy hover:bg-midnight-navy/90"
            data-testid="new-itinerary-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Itinerary
          </Button>
        </div>

        {/* New Itinerary Form */}
        {showNewItinerary && (
          <div className="bg-white p-8 rounded-sm border border-slate-200 mb-8">
            <h2 className="luxury-text text-2xl font-medium mb-6">Create New Itinerary</h2>
            <form onSubmit={handleCreateItinerary} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="event_name">Event Name</Label>
                  <Input id="event_name" name="event_name" required data-testid="itinerary-event-name" />
                </div>
                <div>
                  <Label htmlFor="event_type">Event Type</Label>
                  <Select name="event_type" defaultValue="conference">
                    <SelectTrigger data-testid="itinerary-event-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="incentive">Incentive Trip</SelectItem>
                      <SelectItem value="exhibition">Exhibition</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="start_date">Check-In</Label>
                  <DatePicker
                    selected={itineraryStartDate}
                    onChange={(date) => setItineraryStartDate(date)}
                    selectsStart
                    startDate={itineraryStartDate}
                    endDate={itineraryEndDate}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Check-in date"
                    className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                    calendarClassName="custom-calendar"
                    wrapperClassName="w-full"
                    required
                    data-testid="itinerary-start-date"
                  />
                  <input type="hidden" name="start_date" value={itineraryStartDate ? itineraryStartDate.toISOString().split('T')[0] : ''} />
                </div>
                <div>
                  <Label htmlFor="end_date">Check-Out</Label>
                  <DatePicker
                    selected={itineraryEndDate}
                    onChange={(date) => setItineraryEndDate(date)}
                    selectsEnd
                    startDate={itineraryStartDate}
                    endDate={itineraryEndDate}
                    minDate={itineraryStartDate || new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Check-out date"
                    className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                    calendarClassName="custom-calendar"
                    wrapperClassName="w-full"
                    required
                    data-testid="itinerary-end-date"
                  />
                  <input type="hidden" name="end_date" value={itineraryEndDate ? itineraryEndDate.toISOString().split('T')[0] : ''} />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input id="location" name="location" required data-testid="itinerary-location" />
                </div>
                <div>
                  <Label htmlFor="attendees">Number of Attendees</Label>
                  <Input id="attendees" name="attendees" type="number" required data-testid="itinerary-attendees" />
                </div>
                <div>
                  <Label htmlFor="budget">Total Budget (₹)</Label>
                  <Input id="budget" name="budget" type="number" step="0.01" required data-testid="itinerary-budget" />
                </div>
              </div>
              <div className="flex gap-4">
                <Button type="submit" className="rounded-sm bg-midnight-navy" data-testid="create-itinerary-submit">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create with AI Suggestions
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowNewItinerary(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Itinerary Editor */}
        {currentItinerary && (
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-sm border border-slate-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="luxury-text text-2xl font-medium">{currentItinerary.event_name}</h2>
                <div className="text-right">
                  <p className="text-sm text-slate-800">Total Cost</p>
                  <p className="luxury-text text-3xl font-semibold text-midnight-navy">
                    ₹{totalCost.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-800">Budget: ₹{currentItinerary.budget.toLocaleString()}</p>
                </div>
              </div>

              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-4 items-end p-4 bg-slate-50 rounded-sm">
                    <div className="col-span-1">
                      <Label>Day</Label>
                      <Input
                        type="number"
                        value={item.day}
                        onChange={(e) => updateItem(index, 'day', e.target.value)}
                        min="1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={item.time}
                        onChange={(e) => updateItem(index, 'time', e.target.value)}
                      />
                    </div>
                    <div className="col-span-4">
                      <Label>Activity</Label>
                      <Input
                        value={item.activity}
                        onChange={(e) => updateItem(index, 'activity', e.target.value)}
                        placeholder="Registration, Session, Lunch..."
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Cost (₹)</Label>
                      <Input
                        type="number"
                        value={item.cost}
                        onChange={(e) => updateItem(index, 'cost', e.target.value)}
                        step="0.01"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Notes</Label>
                      <Input
                        value={item.notes}
                        onChange={(e) => updateItem(index, 'notes', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mt-6">
                <Button onClick={addItem} variant="outline" className="rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Activity
                </Button>
                <Button onClick={saveItinerary} className="rounded-sm bg-midnight-navy" data-testid="save-itinerary-button">
                  Save Itinerary
                </Button>
                <Link to={`/itinerary-planner/${currentItinerary.id}`}>
                  <Button className="rounded-sm bg-gold text-navy hover:bg-gold-light">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Open Full Planner
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Existing Itineraries List */}
        {!currentItinerary && itineraries.length > 0 && (
          <div className="space-y-6">
            <h2 className="luxury-text text-2xl font-medium mb-6">Your Event Itineraries</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {itineraries.map(itinerary => (
                <Card
                  key={itinerary.id}
                  className="border border-slate-200 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`itinerary-${itinerary.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg font-semibold text-navy group-hover:text-gold transition-colors">
                        {itinerary.event_name}
                      </CardTitle>
                      <Badge variant={itinerary.status === 'draft' ? 'secondary' : 'default'}>
                        {itinerary.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500">{itinerary.event_type}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gold" />
                        <span>{new Date(itinerary.start_date).toLocaleDateString()} - {new Date(itinerary.end_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gold" />
                        <span>{itinerary.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gold" />
                        <span>{itinerary.attendees} attendees</span>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t">
                      <div className="text-center p-2 bg-slate-50 rounded">
                        <Users className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                        <span className="text-xs text-slate-600">{itinerary.guests?.length || 0} Guests</span>
                      </div>
                      <div className="text-center p-2 bg-slate-50 rounded">
                        <Hotel className="w-4 h-4 mx-auto text-green-500 mb-1" />
                        <span className="text-xs text-slate-600">{itinerary.rooms?.length || 0} Rooms</span>
                      </div>
                      <div className="text-center p-2 bg-slate-50 rounded">
                        <Gift className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                        <span className="text-xs text-slate-600">{itinerary.gifts?.length || 0} Gifts</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2">
                      <div>
                        <span className="text-xs text-slate-500">Budget</span>
                        <p className="text-lg font-bold text-navy">₹{itinerary.budget?.toLocaleString()}</p>
                      </div>
                      <Link to={`/itinerary-planner/${itinerary.id}`}>
                        <Button className="bg-gold hover:bg-gold-light text-navy font-semibold">
                          Open Planner <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {!currentItinerary && itineraries.length === 0 && !showNewItinerary && (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <CalendarDays className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-navy mb-2">No Itineraries Yet</h3>
            <p className="text-slate-600 mb-6">Create your first event itinerary and start planning!</p>
            <Button onClick={() => setShowNewItinerary(true)} className="bg-gold hover:bg-gold-light text-navy font-bold">
              <Plus className="w-4 h-4 mr-2" /> Create Your First Itinerary
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
